import type { Insight, InsightKind } from "@ti/core";
import { Link } from "@tanstack/react-router";
import { ArrowDownRight, ArrowUpRight, Check, Compass, Eye, Lightbulb, ShieldAlert, Sparkle, TrendingUp, Waves, X } from "lucide-react";
import type { ReactNode } from "react";
import { TimeAgo } from "@/components/domain/misc";
import { SeverityPill } from "@/components/domain/status";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useUpdateInsight } from "@/lib/api";
import { cn } from "@/lib/utils";

export const INSIGHT_KIND: Record<InsightKind, { label: string; icon: ReactNode; tone: BadgeTone }> = {
  regression: { label: "Regression", icon: <ArrowDownRight />, tone: "critical" },
  improvement: { label: "Improvement", icon: <ArrowUpRight />, tone: "good" },
  trend: { label: "Trend", icon: <TrendingUp />, tone: "info" },
  anomaly: { label: "Anomaly", icon: <Waves />, tone: "warning" },
  opportunity: { label: "Opportunity", icon: <Lightbulb />, tone: "accent" },
  risk: { label: "Risk", icon: <ShieldAlert />, tone: "serious" },
};

export const INSIGHT_KINDS = Object.keys(INSIGHT_KIND) as InsightKind[];

export function InsightKindBadge({ kind }: { kind: InsightKind }) {
  const k = INSIGHT_KIND[kind];
  return (
    <Badge tone={k.tone}>
      {k.icon}
      {k.label}
    </Badge>
  );
}

/** Metric delta colored by whether the change is good; neutral when the feed doesn't say. */
function MetricDelta({ delta, good }: { delta: string; good?: boolean }) {
  const color = good === undefined ? "text-fg-3" : good ? "text-good-text" : "text-critical-text";
  return (
    <span className={cn("text-xs font-medium tabular", color)}>
      {delta}
      {good !== undefined ? <span className="sr-only">{good ? " (better)" : " (worse)"}</span> : null}
    </span>
  );
}

export function InsightCard({ insight, agentNames }: { insight: Insight; agentNames: Map<string, string> }) {
  const update = useUpdateInsight();
  const isNew = insight.status === "new";
  return (
    <Card className={cn("relative overflow-hidden", isNew && "border-accent/40")}>
      {isNew ? <span aria-hidden className="absolute inset-y-0 left-0 w-0.5 bg-accent" /> : null}
      <div className="flex flex-col gap-3 px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-fg-3">
            <InsightKindBadge kind={insight.kind} />
            <SeverityPill severity={insight.importance} />
            {isNew ? (
              <Badge tone="accent">
                <Sparkle /> New
              </Badge>
            ) : null}
            {insight.agentIds.length ? <span className="font-mono text-[11.5px] text-fg-2">{insight.agentIds.map((a) => agentNames.get(a) ?? a).join(", ")}</span> : null}
            <span>
              · <TimeAgo iso={insight.createdAt} className="text-fg-3" />
            </span>
          </div>
          <div className="flex items-center gap-1">
            {isNew ? (
              <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: insight.id, status: "read" })} disabled={update.isPending}>
                <Check /> Mark read
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 text-xs text-fg-3">
                <Eye className="size-3.5" aria-hidden /> Read
              </span>
            )}
            <Button size="icon-sm" variant="ghost" onClick={() => update.mutate({ id: insight.id, status: "dismissed" })} disabled={update.isPending} aria-label={`Dismiss “${insight.title}”`} title="Dismiss">
              <X />
            </Button>
          </div>
        </div>
        <div>
          <h3 className={cn("text-[14px] text-fg", isNew ? "font-semibold" : "font-medium")}>{insight.title}</h3>
          <p className="mt-1 text-[13px] text-fg-2">{insight.body}</p>
        </div>
        {insight.metrics.length ? (
          <dl className="flex flex-wrap gap-2">
            {insight.metrics.map((m) => (
              <div key={m.label} className="min-w-[132px] rounded-md border bg-surface-2 px-2.5 py-1.5">
                <dt className="text-[11px] text-fg-3">{m.label}</dt>
                <dd className="mt-0.5 flex items-baseline gap-2">
                  <span className="text-[15px] font-semibold tabular text-fg">{m.value}</span>
                  {m.delta ? <MetricDelta delta={m.delta} good={m.good} /> : null}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        {insight.links.length ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <Compass className="size-3.5 text-fg-3" aria-hidden />
            {insight.links.map((l) => (
              <Link key={l.href} to={l.href as string} onClick={() => isNew && update.mutate({ id: insight.id, status: "read" })} className="inline-flex items-center gap-0.5 text-fg-2 hover:text-accent hover:underline">
                {l.label} <ArrowUpRight className="size-3 text-fg-3" aria-hidden />
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
